import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Divider,
  Chip,
  CircularProgress,
  Stack,
  Card,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  LocalShipping,
  LocationOn,
} from '@mui/icons-material';
import useApiCall from '../../../services/api/useApiCall';
import { buildApiConfig } from '../../../services/api/apiClient';
import { endPointEnums } from '../../../services/api/endpoints';
import { useTranslation } from '@/kit/useTranslation';

export default function TransactionDetailsModal({ open, onClose, transaction }) {
  const [parcel, setParcel] = useState(null);
  const [loadingParcel, setLoadingParcel] = useState(false);
  const { makeApiCall } = useApiCall();
  const { t } = useTranslation();

  // Extract parcel ID from transaction description or metadata
  useEffect(() => {
    if (!open || !transaction) {
      setParcel(null);
      return;
    }

    const fetchParcel = async () => {
      // Try to extract parcel ID from multiple possible fields
      let parcelId =
        transaction.parcelId ||
        transaction.parcel_id ||
        transaction.data?.parcelId ||
        transaction.metadata?.parcelId ||
        transaction.relatedParcelId;

      // If not in direct fields, try to extract from description like "Payment for parcel #123"
      if (!parcelId && transaction.description) {
        const match = transaction.description.match(/parcel\s+#?(\d+)/i);
        if (match) {
          parcelId = parseInt(match[1]);
        }
      }

      // Try to extract from orderId if it follows pattern with parcel ID
      if (!parcelId && transaction.orderId) {
        const match = transaction.orderId.match(/(\d+)/);
        if (match) {
          parcelId = parseInt(match[1]);
        }
      }

      if (!parcelId) return;

      try {
        setLoadingParcel(true);
        const config = buildApiConfig(endPointEnums.GET_PARCEL_BY_ID, {
          id: parcelId,
        });
        const response = await makeApiCall(config);
        if (response?.data) {
          setParcel(response.data);
        }
      } catch (error) {
        console.error('[TransactionDetailsModal] Error fetching parcel:', error);
      } finally {
        setLoadingParcel(false);
      }
    };

    fetchParcel();
  }, [open, transaction, makeApiCall]);

  if (!transaction) return null;

  const isCredit = transaction.type === 'credit';

  // Generate appropriate title
  let dialogTitle = t('payment.transactions.transactionDetails');
  if (isCredit) {
    dialogTitle = t('payment.transactions.walletTopUpDetails');
  } else if (transaction.category === 'booking' && (transaction.parcelId || parcel)) {
    dialogTitle = t('payment.transactions.bookingDetailsWithId', {
      id: transaction.parcelId || parcel?.parcelId || '',
    });
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {dialogTitle}
          <IconButton onClick={onClose} size="small" aria-label="Close transaction details">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2}>
          {/* Amount Section */}
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              background: isCredit
                ? 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)'
                : 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
              textAlign: 'center',
            }}
          >
            <Box display="flex" justifyContent="center" mb={1}>
              {isCredit ? (
                <ArrowDownwardIcon sx={{ color: 'success.main', fontSize: 40 }} />
              ) : (
                <ArrowUpwardIcon sx={{ color: 'error.main', fontSize: 40 }} />
              )}
            </Box>
            <Typography
              variant="h4"
              fontWeight="bold"
              color={isCredit ? 'success.main' : 'error.main'}
            >
              {isCredit ? '+' : '-'}₹{parseFloat(transaction.amount).toFixed(2)}
            </Typography>
            <Chip
              label={isCredit ? t('payment.transactions.moneyAdded') : t('payment.payment')}
              color={isCredit ? 'success' : 'error'}
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>

          <Divider />

          {/* Transaction Details */}
          <Box>
            <DetailRow
              label={t('payment.transactions.transactionId')}
              value={transaction.transactionId || transaction.id || 'N/A'}
            />
            <DetailRow label={t('payment.transactions.dateAndTime')} value={transaction.date} />
            <DetailRow
              label={t('payment.transactions.description')}
              value={transaction.description}
            />
            <DetailRow
              label={t('payment.transactions.status')}
              value={
                <Chip
                  label={transaction.status || t('payment.transactions.completed')}
                  color="success"
                  size="small"
                />
              }
            />
            {transaction.orderId && (
              <DetailRow label={t('payment.transactions.orderId')} value={transaction.orderId} />
            )}
            {transaction.paymentMethod && (
              <DetailRow
                label={t('payment.transactions.paymentMethod')}
                value={transaction.paymentMethod}
              />
            )}
            {transaction.balanceAfter !== undefined && (
              <DetailRow
                label={t('payment.transactions.balanceAfter')}
                value={`₹${parseFloat(transaction.balanceAfter).toFixed(2)}`}
              />
            )}
          </Box>

          {/* Parcel Details Section */}
          {loadingParcel && (
            <Box display="flex" justifyContent="center" py={2}>
              <CircularProgress size={24} />
              <Typography variant="caption" color="text.secondary" ml={2}>
                {t('payment.transactions.loadingParcelDetails')}
              </Typography>
            </Box>
          )}

          {!loadingParcel &&
            !parcel &&
            transaction.description &&
            transaction.description.toLowerCase().includes('parcel') && (
              <Box
                sx={(theme) => ({
                  textAlign: 'center',
                  py: 2,
                  bgcolor:
                    theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f5f5f5',
                  borderRadius: 2,
                })}
              >
                <Typography variant="caption" color="text.secondary">
                  {t('payment.transactions.parcelInfoNotAvailable')}
                </Typography>
              </Box>
            )}

          {parcel && !loadingParcel && (
            <>
              <Divider sx={{ my: 1 }} />
              <Box>
                <Typography
                  variant="subtitle2"
                  fontWeight={700}
                  mb={2}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                >
                  <LocalShipping color="primary" />
                  {t('payment.transactions.parcelDetails')}
                </Typography>

                <Card
                  elevation={0}
                  sx={(theme) => ({
                    bgcolor:
                      theme.palette.mode === 'dark' ? theme.palette.background.default : '#f5f5f5',
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                  })}
                >
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {t('payment.transactions.parcelId')}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        #{parcel.parcelId || parcel.id}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {t('payment.transactions.status')}
                      </Typography>
                      <Box mt={0.5}>
                        <Chip
                          label={parcel.status || 'N/A'}
                          size="small"
                          color={
                            parcel.status === 'DELIVERED'
                              ? 'success'
                              : parcel.status?.includes('TRANSIT')
                                ? 'primary'
                                : 'default'
                          }
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>
                    </Box>

                    {parcel.pickupAddress?.label && (
                      <Box
                        sx={{
                          bgcolor: '#e8f5e9',
                          p: 1.5,
                          borderRadius: 1,
                          border: '1px solid #4caf5030',
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <LocationOn sx={{ fontSize: 18, color: '#4caf50', mt: 0.3 }} />
                          <Box>
                            <Typography variant="caption" color="success.dark" fontWeight={700}>
                              {t('payment.transactions.pickup')}
                            </Typography>
                            <Typography variant="body2" fontSize="0.8rem">
                              {parcel.pickupAddress.label}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}

                    {parcel.deliveryAddress?.label && (
                      <Box
                        sx={{
                          bgcolor: '#fff3e0',
                          p: 1.5,
                          borderRadius: 1,
                          border: '1px solid #ff980030',
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <LocationOn sx={{ fontSize: 18, color: '#ff9800', mt: 0.3 }} />
                          <Box>
                            <Typography variant="caption" color="warning.dark" fontWeight={700}>
                              {t('payment.transactions.delivery')}
                            </Typography>
                            <Typography variant="body2" fontSize="0.8rem">
                              {parcel.deliveryAddress.label}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}

                    {parcel.riderName && (
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          {t('payment.transactions.rider')}
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {parcel.riderName}
                        </Typography>
                      </Box>
                    )}

                    {(parcel.isMultiStage || parcel.totalLegs > 1) && (
                      <Chip
                        icon={<LocalShipping sx={{ fontSize: 14 }} />}
                        label={t('payment.transactions.multiStageLegs', {
                          legs: parcel.totalLegs || 2,
                        })}
                        size="small"
                        sx={{
                          bgcolor: '#e3f2fd',
                          color: '#1976d2',
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Stack>
                </Card>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ label, value }) {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      py={1.5}
      borderBottom="1px solid"
      borderColor="divider"
    >
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography variant="body2" fontWeight="medium" pl={2}>
          {value}
        </Typography>
      ) : (
        value
      )}
    </Box>
  );
}
